import axiosInstance from "../api";
import { notification } from "antd";

// All /custom_email endpoints answer HTTP 200 with { status, status_code, message, data }
// (same convention as the rest of the admin API), so errors are read from the body.
const unwrap = (response, fallback) => {
  if (response?.data?.status_code === 200) return response.data.data;
  const err = new Error(response?.data?.message || fallback);
  err.apiMessage = response?.data?.message || fallback;
  throw err;
};

const notifyError = (title, error, fallback) => {
  notification.error({
    message: title,
    description: error?.apiMessage || error?.response?.data?.message || fallback,
    duration: 4,
  });
};

export const listCustomEmails = async ({ skip = 0, limit = 10, search = "", start_date = null, end_date = null } = {}) => {
  const params = new URLSearchParams({ skip, limit });
  if (search) params.append("search", search);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  try {
    return unwrap(await axiosInstance.get(`/custom_email/list?${params}`), "Failed to fetch custom emails.");
  } catch (e) {
    notifyError("Error Fetching Custom Emails", e, "Failed to fetch custom emails.");
    throw e;
  }
};

export const exportCustomEmails = async ({ search = "", start_date = null, end_date = null } = {}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  try {
    const response = await axiosInstance.get(`/custom_email/export?${params}`, { responseType: "blob" });
    if (response.headers["content-type"]?.includes("application/json")) {
      const body = JSON.parse(await response.data.text());
      throw Object.assign(new Error(body.message), { apiMessage: body.message });
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "custom_email_campaigns.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    notifyError("Error Exporting Custom Emails", e, "Failed to export.");
    throw e;
  }
};

// One page (50) of OneSignal templates: { templates, total_count, next_offset (null on the last page) }.
export const listTemplates = async (offset = 0) => {
  try {
    return unwrap(await axiosInstance.get(`/custom_email/templates?offset=${offset}&limit=50`), "Failed to load OneSignal templates.");
  } catch (e) {
    notifyError("Error Loading Templates", e, "Failed to load OneSignal templates.");
    throw e;
  }
};

export const getTemplate = async (templateId) => {
  try {
    return unwrap(await axiosInstance.get(`/custom_email/templates/${templateId}`), "Failed to load the template.");
  } catch (e) {
    notifyError("Error Loading Template", e, "Failed to load the template.");
    throw e;
  }
};

// Preview and send validation errors are shown inline on the page, so these don't toast.
export const previewCustomEmail = async (payload) =>
  unwrap(await axiosInstance.post(`/custom_email/preview`, payload), "Preview failed.");

export const testSendCustomEmail = async (payload) =>
  unwrap(await axiosInstance.post(`/custom_email/test_send`, payload), "Test email failed.");

export const sendCustomEmail = async (payload) => {
  const response = await axiosInstance.post(`/custom_email/send`, payload);
  const data = unwrap(response, "Send failed.");
  return { ...data, message: response.data.message };
};

export const getCustomEmail = async (campaignId) => {
  try {
    return unwrap(await axiosInstance.get(`/custom_email/campaign/${campaignId}`), "Failed to load the campaign.");
  } catch (e) {
    notifyError("Error Loading Campaign", e, "Failed to load the campaign.");
    throw e;
  }
};

export const getCustomEmailRecipients = async (campaignId, { skip = 0, limit = 10, search = "", status = null } = {}) => {
  const params = new URLSearchParams({ skip, limit });
  if (search) params.append("search", search);
  if (status) params.append("status", status);
  try {
    return unwrap(await axiosInstance.get(`/custom_email/campaign/${campaignId}/recipients?${params}`), "Failed to load recipients.");
  } catch (e) {
    notifyError("Error Loading Recipients", e, "Failed to load recipients.");
    throw e;
  }
};

export const getCustomEmailRecipient = async (campaignId, recipientId) => {
  try {
    return unwrap(await axiosInstance.get(`/custom_email/campaign/${campaignId}/recipient/${recipientId}`), "Failed to load the recipient.");
  } catch (e) {
    notifyError("Error Loading Recipient", e, "Failed to load the recipient.");
    throw e;
  }
};

export const listCustomEmailFiles = async () => {
  try {
    return unwrap(await axiosInstance.get(`/custom_email/files`), "Failed to load files.").files;
  } catch (e) {
    notifyError("Error Loading Files", e, "Failed to load files.");
    throw e;
  }
};

export const uploadCustomEmailFile = async (file) => {
  const form = new FormData();
  form.append("file", file);
  try {
    return unwrap(
      await axiosInstance.post(`/custom_email/files`, form, { headers: { "Content-Type": "multipart/form-data" } }),
      "Upload failed."
    );
  } catch (e) {
    notifyError("Upload Failed", e, "Upload failed.");
    throw e;
  }
};
